import {createSlice, PayloadAction} from "@reduxjs/toolkit";

export interface ScadaFilterValues {
  inAgencies: number[];
  interventionDate: string | null;
  notInPlantStatusTypes: string[];
  notSynchronised: boolean;
  startOperationDate: string | null;
  endOperationDate: string | null;
  platform: string | null;
  plantTags: string[];
  equipments: string[];
  checkpointStatuses: string[];
  alarmReferences: string[];
}

// Filtres propres aux graphiques d'alarmes : ils trient les alarmes déjà chargées et ne doivent pas
// relancer les requêtes de ChartArea, qui suit `value`. Ils vivent donc à côté.
export interface AlarmChartFilters {
  hiddenEquipmentTypes: string[];
  distinguishOld: boolean;
}

interface ScadaFilterValuesState {
  value: ScadaFilterValues;
  alarmCharts: AlarmChartFilters;
}

export const defaultScadaFilterValues: ScadaFilterValues = {
  inAgencies: [],
  interventionDate: null,
  notInPlantStatusTypes: [],
  notSynchronised: true,
  startOperationDate: null,
  endOperationDate: null,
  platform: null,
  plantTags: [],
  equipments: [],
  checkpointStatuses: [],
  alarmReferences: [],
};

export const defaultAlarmChartFilters: AlarmChartFilters = {
  hiddenEquipmentTypes: [],
  distinguishOld: false,
};

const initialState: ScadaFilterValuesState = {
  value: defaultScadaFilterValues,
  alarmCharts: defaultAlarmChartFilters,
};

// Les sélecteurs d'agences et de tags renvoient leur sélection initiale une fois chargés : sans ce
// garde-fou, ce nouveau tableau au contenu identique relancerait les requêtes de ChartArea.
const sameValues = <T>(a: T[], b: T[]): boolean => a.length === b.length && a.every((x) => b.includes(x));

const scadaFilterValuesSlice = createSlice({
  name: "scadaFilterValues",
  initialState,
  reducers: {
    setFilterValues: (state, action: PayloadAction<ScadaFilterValues>) => {
      state.value = action.payload;
    },
    setInAgencies: (state, action) => {
      if (!sameValues(state.value.inAgencies, action.payload)) {
        state.value.inAgencies = action.payload;
      }
    },
    setInterventionDate: (state, action) => {
      state.value.interventionDate = action.payload;
    },
    setNotSynchronised: (state, action) => {
      state.value.notSynchronised = action.payload;
    },
    removeNotInPlantStatusTypes: (state, action) => {
      state.value.notInPlantStatusTypes = state.value.notInPlantStatusTypes.filter((x) => x !== action.payload);
    },
    addNotInPlantStatusTypes: (state, action) => {
      state.value.notInPlantStatusTypes.push(action.payload);
    },
    setStartOperationDate: (state, action) => {
      state.value.startOperationDate = action.payload;
    },
    setEndOperationDate: (state, action) => {
      state.value.endOperationDate = action.payload;
    },
    setPlatform: (state, action) => {
      state.value.platform = action.payload;
    },
    setPlantTags: (state, action) => {
      if (!sameValues(state.value.plantTags, action.payload)) {
        state.value.plantTags = action.payload;
      }
    },
    setEquipments: (state, action) => {
      state.value.equipments = action.payload;
    },
    setCheckpointStatuses: (state, action) => {
      state.value.checkpointStatuses = action.payload;
    },
    setAlarmReferences: (state, action: PayloadAction<string[]>) => {
      state.value.alarmReferences = action.payload;
    },
    // Écarte les valeurs venues de l'URL qui n'existent plus dans les référentiels (équipement
    // supprimé, type d'alarme retiré, agence hors de portée de l'utilisateur…). Un tableau n'est
    // remplacé que s'il perd une valeur, pour ne pas relancer les requêtes de ChartArea.
    removeUnknownValues: (
      state,
      action: PayloadAction<{
        agencies: number[];
        plantStatusTypes: string[];
        equipments: string[];
        alarmReferences: string[];
      }>,
    ) => {
      const {agencies, plantStatusTypes, equipments, alarmReferences} = action.payload;
      const keepKnown = <T>(values: T[], known: T[]) => {
        const kept = values.filter((x) => known.includes(x));
        return kept.length === values.length ? values : kept;
      };
      state.value.inAgencies = keepKnown(state.value.inAgencies, agencies);
      state.value.notInPlantStatusTypes = keepKnown(state.value.notInPlantStatusTypes, plantStatusTypes);
      state.value.equipments = keepKnown(state.value.equipments, equipments);
      state.value.alarmReferences = keepKnown(state.value.alarmReferences, alarmReferences);
    },
    setAlarmChartFilters: (state, action: PayloadAction<AlarmChartFilters>) => {
      state.alarmCharts = action.payload;
    },
    setHiddenAlarmEquipmentTypes: (state, action: PayloadAction<string[]>) => {
      state.alarmCharts.hiddenEquipmentTypes = action.payload;
    },
    setDistinguishOldAlarms: (state, action: PayloadAction<boolean>) => {
      state.alarmCharts.distinguishOld = action.payload;
    },
  },
});

export const {
  setFilterValues,
  setInAgencies,
  setInterventionDate,
  setNotSynchronised,
  removeNotInPlantStatusTypes,
  addNotInPlantStatusTypes,
  setStartOperationDate,
  setEndOperationDate,
  setPlatform,
  setPlantTags,
  setEquipments,
  setCheckpointStatuses,
  setAlarmReferences,
  removeUnknownValues,
  setAlarmChartFilters,
  setHiddenAlarmEquipmentTypes,
  setDistinguishOldAlarms,
} = scadaFilterValuesSlice.actions;

export default scadaFilterValuesSlice.reducer;
